// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-20
// protected: NO  -> D-025 Role Email Matrix (OWNER-managed). Ganti approve manual.
// =============================================================================
// User Management (D-025): matrix Role | Email 1 | Email 2 | Status | Action.
// Owner isi email -> Save -> email langsung bisa login Google & otomatis dapat role.
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Loader2, Save, CheckCircle2, Clock, AlertCircle, Crown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { TableScroll } from "@/v2/components/TableScroll";
import { EmptyState, TableSkeletonRows, useDelayedLoading } from "@/v2/components/ux";

const SlotStatus = ({ slot }) => {
  if (!slot.email) return <span className="text-xs text-muted-foreground">—</span>;
  if (slot.linked_profile && slot.active && slot.role_synced)
    return <Badge variant="secondary" className="gap-1 text-[10px]"><CheckCircle2 className="w-3 h-3" />Aktif</Badge>;
  if (slot.linked_profile)
    return <Badge variant="outline" className="gap-1 text-[10px]"><AlertCircle className="w-3 h-3" />Perlu refresh</Badge>;
  return <Badge variant="outline" className="gap-1 text-[10px]"><Clock className="w-3 h-3" />Belum login</Badge>;
};

export default function RoleMatrixPage() {
  const { api } = useV2Auth();
  const [roles, setRoles] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [draft, setDraft] = useState({}); // key `${role_key}#${slot}` -> email
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/role-slots");
      if (!r.ok) { toast.error("Gagal memuat matrix"); return; }
      const j = await r.json();
      setRoles(j.roles || []);
      setOwnerEmail(j.owner_email || "");
      const d = {};
      (j.roles || []).forEach((role) =>
        role.slots.forEach((s) => { d[`${role.role_key}#${s.slot_no}`] = s.email || ""; }));
      setDraft(d);
    } catch (e) { toast.error("Gagal memuat matrix"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const setEmail = (roleKey, slot, val) =>
    setDraft((d) => ({ ...d, [`${roleKey}#${slot}`]: val }));

  const saveEntries = async (entries, key) => {
    setSavingKey(key);
    try {
      const r = await api("/api/v2/role-slots", {
        method: "PUT",
        body: JSON.stringify({ entries }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = typeof j.detail === "string" ? j.detail : "Gagal menyimpan";
        toast.error(msg);
        return;
      }
      toast.success(`Tersimpan (${j.changes ?? 0} perubahan)`);
      await load();
    } catch (e) { toast.error("Gagal menyimpan"); }
    finally { setSavingKey(""); }
  };

  const saveRole = (role) => {
    const entries = role.slots.map((s) => ({
      role_key: role.role_key, slot_no: s.slot_no,
      email: draft[`${role.role_key}#${s.slot_no}`] || null,
    }));
    saveEntries(entries, role.role_key);
  };

  const saveAll = () => {
    const entries = [];
    roles.forEach((role) => role.slots.forEach((s) => entries.push({
      role_key: role.role_key, slot_no: s.slot_no,
      email: draft[`${role.role_key}#${s.slot_no}`] || null,
    })));
    saveEntries(entries, "__all__");
  };

  const showSkeleton = useDelayedLoading(loading);
  if (loading) {
    return showSkeleton ? (
      <div className="space-y-6" data-testid="role-matrix-loading">
        <div className="h-8 w-72 rounded-md bg-primary/10 animate-pulse" />
        <Card className="overflow-hidden">
          <Table><TableBody><TableSkeletonRows rows={5} cols={5} testid="role-matrix-skeleton" /></TableBody></Table>
        </Card>
      </div>
    ) : null;
  }

  return (
    <div className="space-y-6" data-testid="role-matrix-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />Manajemen Pengguna
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Isi email pada slot role lalu simpan. Email tersebut langsung dapat login Google
            dan otomatis memperoleh role sesuai slot — tanpa proses persetujuan manual.
            Maksimal 2 email per role. Mengganti/mengosongkan email akan mencabut akses email lama.
          </p>
        </div>
        <Button onClick={saveAll} disabled={savingKey === "__all__"} data-testid="save-all-btn">
          {savingKey === "__all__" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Simpan Semua
        </Button>
      </div>

      {/* D-026: Owner Utama (Super Owner) — read-only, dari env OWNER_EMAIL, tidak bisa diubah/dihapus */}
      <Card className="p-4 border-primary/30 bg-primary/5" data-testid="super-owner-card">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="grid place-items-center w-10 h-10 rounded-xl bg-primary/15 text-primary shrink-0">
            <Crown className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Owner Utama / Super Owner</span>
              <Badge variant="secondary" className="gap-1 text-[10px]"><ShieldCheck className="w-3 h-3" />Read-only</Badge>
            </div>
            <div className="text-sm text-muted-foreground truncate" data-testid="super-owner-email">
              {ownerEmail || "(belum diset di environment)"}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground sm:ml-auto max-w-md">
            Diatur via environment, tidak bisa diubah/dihapus dari sini. Hanya Super Owner yang dapat
            menjalankan <b>Reset Data Simulasi</b>. Owner Tambahan adalah Owner operasional (tanpa hak reset).
          </p>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <TableScroll>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Role</TableHead>
              <TableHead>Email 1</TableHead>
              <TableHead>Email 2</TableHead>
              <TableHead className="w-[150px]">Status</TableHead>
              <TableHead className="w-[120px] text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 && (
              <TableRow><TableCell colSpan={5} className="p-0">
                <EmptyState icon={Users} title="Belum ada role terkonfigurasi"
                  description="Daftar role dikelola oleh sistem. Jika kosong, hubungi Owner Utama atau periksa konfigurasi environment."
                  testid="role-matrix-empty" />
              </TableCell></TableRow>
            )}
            {roles.map((role) => (
              <TableRow key={role.role_key} data-testid={`role-row-${role.role_key}`} className="hover:bg-muted/50 transition-colors">
                <TableCell>
                  <div className="font-medium">{role.label}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {role.region}{role.store ? ` · Store ${role.store}` : ""}
                  </div>
                </TableCell>
                {role.slots.map((s) => (
                  <TableCell key={s.slot_no}>
                    <Input
                      type="email"
                      placeholder={role.role_key === "OWNER" ? `Owner Tambahan ${s.slot_no} — email@contoh.com` : "email@contoh.com"}
                      value={draft[`${role.role_key}#${s.slot_no}`] ?? ""}
                      onChange={(e) => setEmail(role.role_key, s.slot_no, e.target.value)}
                      data-testid={`email-${role.role_key}-${s.slot_no}`}
                      className="h-9"
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {role.slots.map((s) => (
                      <SlotStatus key={s.slot_no} slot={s} />
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => saveRole(role)}
                          disabled={savingKey === role.role_key}
                          data-testid={`save-${role.role_key}`}>
                    {savingKey === role.role_key
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableScroll>
      </Card>
    </div>
  );
}
